import * as z from "zod";
import { assistantModelIds } from "./assistant/models";
import { TIL_OUTCOME_MINUTES } from "./til-enums";

// The parameters of `platform.config_parameter` (FR-M14-03, ADR-077): thresholds and timings a domain reads, an
// administrator changes without a release. One list for both: a domain cannot read a key the administration does not
// know, and the administration accepts only the values the domain understands.

interface Parameter<S extends z.ZodType = z.ZodType> {
  /** The values the domain understands: any other one, stored or asked for, counts as never set. */
  value: S;
  /** What the domain assumes until the parameter is set. */
  default: z.output<S>;
  /** What a number counts, for the screen that edits it. */
  unit?: "days" | "minutes";
}

/** A parameter; its unit stays in its type, so a rule that counts minutes names only a parameter in minutes. */
const parameter = <S extends z.ZodType, U extends Parameter["unit"] = undefined>(
  value: S,
  fallback: z.output<S>,
  unit?: U,
) => ({ value, default: fallback, ...(unit && { unit }) }) as Parameter<S> & (U extends string ? { unit: U } : unknown);

/** Whole days, at least `min`; ten years is more than any deadline. */
const days = (min = 0) => z.number().int().min(min).max(3650);
/** A week is more than any alarm waits. */
const minutes = z.number().positive().max(10_080);

export const parameters = {
  /** Tomorrow's wholesale prices rounded up to the unit (data-model README §7.1). */
  "pricing.wholesale_roundup": parameter(z.boolean(), false),
  /** The retail prices of invested brands rounded up to the nearest 1 or 10. */
  "pricing.invested_retail_roundup": parameter(z.union([z.literal(1), z.literal(10)]), 1),
  /**
   * Whether the SKUs changed after their last file to the ERP go again, every day, in a file of their own (spec v0.3
   * §7.3.4, ADR-257): off, only a sync of the linesheet or of the attributes sends them again.
   */
  "catalog.erp_item_resend": parameter(z.boolean(), false),
  /** The hour on Tomorrow's clock (Europe/Rome) at which the SKUs to send again to the ERP go, if the resend is on. */
  "catalog.erp_item_resend_hour": parameter(z.number().int().min(0).max(23), 2),
  // TODO(V-22): the cadence of the manual import is not agreed yet (B-115); a working day is the default of the spec
  /**
   * Working days after which a file to the ERP still not imported is told to who imports and to Finance, once (spec
   * v0.3 §7.5, ADR-258).
   */
  "integration.erp_file_overdue_working_days": parameter(days(1), 1, "days"),
  /**
   * Whether the access via MCP is on for everyone (ADR-263): off, no assistant connects, no personal token is made,
   * and every call to the MCP server is refused at once, whatever the roles say.
   */
  "mcp.enabled": parameter(z.boolean(), true),
  /**
   * Whether the assistant answers in the rooms of Backstage (ADR-284): off, nobody asks it anything. Off until an
   * administrator turns it on; in production only after the review of P7.1 (B-135).
   */
  "assistant.enabled": parameter(z.boolean(), false),
  /** The model the assistant answers with, from the list of `@nexxt/contracts/assistant`. */
  "assistant.model": parameter(z.enum(assistantModelIds), "claude-opus-5-5"),
  /** What the assistant may spend in a month (UTC), in USD: past it, it answers again the month after (B-133). */
  "assistant.monthly_cap_usd": parameter(z.number().positive().max(100_000), 500),
  // TODO(D-10): the business has not said the number of days yet; 30 is the provisional value of B-15
  /** Days before the inbound delivery window opens by which the SKU attributes are due (FR-M4-08). */
  "attributes.due_days_before_delivery": parameter(days(), 30, "days"),
  /**
   * Days before its due day on which the brand hears first of an attribute task not complete; then every
   * `attributes.reminder_every_days`, and on the due day (B-74). 0 reminds only on the due day.
   */
  "attributes.first_reminder_days_before_due": parameter(days(), 14, "days"),
  /** Days between two reminders of an attribute task before it is due. */
  "attributes.reminder_every_days": parameter(days(1), 7, "days"),
  /** Days between two reminders of an overdue attribute task, to the brand and to Operations, from the day after it was due. */
  "attributes.overdue_reminder_every_days": parameter(days(1), 7, "days"),
  /** Reminders of an overdue attribute task at most: 0 sends none, and the task is overdue all the same. */
  "attributes.max_overdue_reminders": parameter(z.number().int().min(0).max(20), 3),
  // TODO(Q-CAT-06): Operations has not confirmed the three timings yet; these are the ones of the current portal
  /** Days before a sales campaign starts from which its brands are invited to load their linesheet. */
  "linesheet_invitation.days_before_campaign": parameter(days(), 21, "days"),
  /**
   * Days after its last email before a brand is reminded: 0 would remind at every run. At most `reminders.max_reminders`
   * reminders, then Operations' queue (B-97, ADR-245).
   */
  "linesheet_invitation.reminder_every_days": parameter(days(1), 2, "days"),
  /** Days before a sales campaign starts by which the brands are asked to submit. */
  "linesheet_invitation.deadline_days_before_campaign": parameter(days(), 7, "days"),
  // 2 for every brand and showroom, as the current portal (B-29, 12/10); why 2 is asked of Sales before the first campaign
  /** Customers a brand takes in a slot beyond one per sales person of its availability (ADR-089). */
  "booking.extra_customers_per_slot": parameter(z.number().int().min(0).max(50), 2),
  // TODO(T-40): TIL has not said how long Business Central takes to register a document and the Document Service to
  // make its PDF; 60 is the outcome of I-04 (15) with room for the two messages that follow
  /** Minutes Business Central and the Document Service have to number a document asked for and make its PDF. */
  "documents.issue_overdue_minutes": parameter(minutes, 60, "minutes"),
  /**
   * Minutes an invoice or a proforma waits for Business Central to register its order (I-03) before it goes in error
   * (ADR-273): meanwhile documents asks for it again at every run of its job, every 2 minutes. 30 is the outcome of
   * I-03 (15) with room.
   */
  "documents.order_wait_minutes": parameter(minutes, 30, "minutes"),
  // TODO(V-18): how a deposit is invoiced and taken off the invoices (B-114); off, the deposit stays an advance of the
  // customer that Business Central applies (B-70)
  /**
   * Whether the invoices file to the ERP takes the deposit of an order off each of its invoices, the same share of each
   * (C-50, ADR-256): a line that is not goods, on the account of the sales setup of the company.
   */
  "documents.erp_deposit_pro_rata": parameter(z.boolean(), false),
  // TODO(T-31): TIL has not said how often the IMS sends a full publication; one a day is the hypothesis, plus an hour
  /**
   * Minutes after which what the IMS said of a SKU in a warehouse is old for the allocation (FR-M8-03, ADR-134): past
   * them the full publication that renews every SKU did not arrive.
   */
  "allocation.atp_stale_minutes": parameter(minutes, 25 * 60, "minutes"),
  // TODO(T-53): Microsys has not said whether the availability of the IMS already leaves out the units on hold; if it
  // does, this goes to false, or Nexxt subtracts them twice
  /**
   * Whether the allocation and the movements subtract from the availability of the IMS the units in more of an inbound,
   * on hold until the supplier invoices them (FR-M11-11, ADR-145).
   */
  "stock.subtract_units_on_hold": parameter(z.boolean(), true),
  // TODO(B-79): 50 units is provisional (13/10); a threshold on value, on the cost of the last PO in EUR, waits for
  // Finance, as do the accounts by sign or warehouse (ADR-226)
  /**
   * Units an adjustment of stock the IMS proposed may add and take in all before Finance approves it after Logistics
   * (B-54 point 1, ADR-226): beyond them it waits for Finance. 0 sends every adjustment to Finance.
   */
  "stock.adjustment_finance_threshold_units": parameter(z.number().int().min(0).max(1_000_000), 50),
  /**
   * Minutes after midnight on Tomorrow's clock (Europe/Rome) by which the IMS completes the full publication of every
   * warehouse it sends each night (B-16, B-92, ADR-242): 60 is 01:00. A warehouse without one by then is told to
   * Logistics and the Customer Service; the night run of the allocation starts all the same.
   */
  "stock.full_publication_deadline_minutes": parameter(z.number().int().min(0).max(1439), 60, "minutes"),
  // TODO(T-50): TIL has not said how long GXO takes to take an instruction; 60 is the outcome of I-09 (15) with room
  /**
   * Minutes after which a shipment released to the warehouse and not yet taken is a queue of the home (FR-M13-06,
   * ADR-152).
   */
  "fulfillment.ack_overdue_minutes": parameter(minutes, 60, "minutes"),
  /**
   * Days after its publication from which a PO the supplier has not confirmed is reminded to it (B-37, ADR-160): the
   * first reminder; then every `reminders.repeat_every_days`, at most `reminders.max_reminders` (ADR-245).
   */
  "purchase_order.confirmation_reminder_after_days": parameter(days(1), 3, "days"),
  // B-68: no cadence today (Credit Control dunns by hand); the numbers of ADR-178 §1.12, validated in the November tests
  /** Days after its proforma was issued from which an order paid in advance and not paid is dunned (ADR-203). */
  "sales_order.payment_reminder_after_days": parameter(days(1), 7, "days"),
  /** Days between two reminders of a proforma not paid (ADR-203). */
  "sales_order.payment_reminder_every_days": parameter(days(1), 7, "days"),
  /**
   * Days after the first order confirmation with a deposit was sent by which the deposit is due (B-64, ADR-247): the
   * confirmation is the document that asks for it. Without one sent, the deposit is not dunned (B-96).
   */
  "sales_order.deposit_due_days": parameter(days(), 14, "days"),
  /**
   * Days after its deposit was due from which an order whose deposit is not paid in full is dunned (ADR-203): 0 dunns
   * it on the day it is due.
   */
  "sales_order.deposit_reminder_after_days": parameter(days(), 7, "days"),
  /** Days between two reminders of a deposit not paid in full (ADR-203). */
  "sales_order.deposit_reminder_every_days": parameter(days(1), 7, "days"),
  /**
   * Reminders of a proforma or a deposit not paid before the order goes to the queue of Credit Control, one cadence
   * after the last (ADR-178 §1.12, ADR-203).
   */
  "sales_order.max_payment_reminders": parameter(z.number().int().min(1).max(20), 3),
  // TODO(B-47): the Customer Service has not validated the 7 days yet
  /**
   * Days after its issue a revision of an order confirmation waits for the customer's answer: past them the customer
   * is reminded, once, and the order enters the queue of the Customer Service (ADR-208). It blocks nothing.
   */
  "sales_order.confirmation_answer_days": parameter(days(1), 7, "days"),
  /**
   * Percent of the net value of the units of an order cancelled that Nexxt proposes as its cancellation fee (B-49
   * point 3, B-72, ADR-222): Credit Control applies it, and may change it.
   */
  "sales_order.cancellation_fee_pct": parameter(z.number().min(0).max(100), 30),
  /**
   * Working days an order allocated waits held by its credit or its payment before Credit Control hears of it, in the
   * daily email of the orders held (B-60, B-73, ADR-222).
   */
  "sales_order.held_alert_working_days": parameter(days(1), 1, "days"),
  // TODO(B-49): Finance has not said whether HighRadius or Integrity tells the payment on Business Central, nor from
  // when; until then the flag follows I-06 as soon as Business Central sends it (ADR-234)
  /**
   * Whether the flag "payment completed" of an order follows what the payment tool on Business Central says through
   * I-06 (B-49 point 1, ADR-234): off, only Credit Control raises and withdraws it, as in F1.
   */
  "sales_order.payment_flag_from_bc": parameter(z.boolean(), true),
  // TODO(T-49, T-73): on at the cutover (ADR-248, runbook), once TIL has I-20 live and the data migration has the ids of
  // the deals of NetSuite; pipeline and stages are the ones of B-85
  /**
   * Whether every sales order goes to HubSpot as a deal (I-20), created once it is born and updated when its total,
   * its units, its stage, its territory or what Business Central invoiced of it change (spec v0.2, area N, ADR-236,
   * ADR-248): off, HubSpot has no deal of the orders of Nexxt.
   */
  "sales_order.deals_to_hubspot": parameter(z.boolean(), false),
  /**
   * Days after Logistics confirmed a shortage from which a supplier that has not chosen for the rest is reminded (B-41):
   * the first reminder of its series (ADR-245).
   */
  "inbound.shortage_reminder_after_days": parameter(days(1), 2, "days"),
  /**
   * Days after a credit note was asked for, or refused, from which its document not sent is reminded (B-41): a refusal
   * starts a series of its own (B-87, ADR-245).
   */
  "credit_note.reminder_after_days": parameter(days(1), 2, "days"),
  /**
   * Days after Logistics opened the inbound of the extra units it accepted from which its draft is reminded (B-42): the
   * first reminder of its series (ADR-245).
   */
  "inbound.extra_units_reminder_after_days": parameter(days(1), 2, "days"),
  // TODO(B-51): to be validated with Logistics; 1 reminds the day after the pickup day, once
  /**
   * Days after the pickup day of an inbound (the one written, or the day the goods are ready) from which its tracking
   * still missing is reminded, once, to who writes it (ADR-212).
   */
  "inbound.tracking_reminder_after_days": parameter(days(1), 1, "days"),
  // TODO(B-188): the assumption of 31/10, to be confirmed by the Customer Service
  /**
   * Days past the expected delivery an inbound was submitted with beyond which a move of it tells the Customer Service,
   * with the sales orders it serves (ADR-323).
   */
  "inbound.delivery_slip_alert_days": parameter(days(1), 7, "days"),
  // TODO(B-50): no tool is chosen yet, and TIL does not know I-35 (T-64): off until both are there (ADR-233)
  /**
   * Whether the packing list of an inbound that reaches Logistics is sent to the extraction tool through TIL (I-35):
   * off, Logistics types every line as in F1.
   */
  "inbound.packing_list_extraction": parameter(z.boolean(), false),
  // TODO(B-50): the default of the answer of 09/10, to be validated with Logistics
  /**
   * The confidence of the extraction tool, from 0 to 1, below which a line it read waits for Logistics: one such line
   * sends the whole packing list to the review (B-50, ADR-233).
   */
  "inbound.extraction_confidence_threshold": parameter(z.number().min(0).max(1), 0.9),
  /**
   * Days after a row of the Price Setup was created from which its tab not confirmed is reminded (FR-M3-01, B-43): the
   * series of a season and tab starts again only once all its rows are confirmed (B-87, ADR-245).
   */
  "price_setup.confirmation_reminder_after_days": parameter(days(1), 2, "days"),
  /**
   * Days between two reminders of what waits on a supplier or on a function of Tomorrow, and after the last one before
   * it waits in the queue of the team that owns it: the 48 hours of every reminder of the current portal (B-37). Not
   * the dunning of an order, whose cadence and cap are of sales (ADR-203).
   */
  "reminders.repeat_every_days": parameter(days(1), 2, "days"),
  /**
   * Reminders at most of a PO not confirmed, the missing units and the credit note of an inbound, the extra units, a tab
   * of the Price Setup and a linesheet invitation (B-37, B-41…B-43, B-97, ADR-245): a cadence after the last one, what
   * still waits goes to the queue of Operations, Logistics or Finance, and the rows of a tab stay highlighted.
   */
  "reminders.max_reminders": parameter(z.number().int().min(1).max(20), 3),
  /**
   * Days after its last email before an invitation not accepted is sent again, with a new link (B-44, ADR-161); a
   * cadence after the last one it waits for Customer Service or Operations (ADR-245).
   */
  "user_invitation.reminder_every_days": parameter(days(1), 2, "days"),
  /**
   * Times an invitation not accepted is sent again (B-44): 0 sends it once. A send by hand from Backstage starts the
   * count again (B-88, ADR-245).
   */
  "user_invitation.max_reminders": parameter(z.number().int().min(0).max(20), 3),
  /**
   * Days the personal link of an e-TAF works after Tomorrow sent it (ADR-175): the current portal's link never expires,
   * and a new one replaces it.
   */
  "etaf.link_valid_days": parameter(days(1), 30, "days"),
  // TODO(B-34): the current portal's booking link never expires; 30 days as the e-TAF's until the business says
  /** Days the personal link to the booking of a campaign works after Tomorrow emailed it (ADR-237). */
  "booking.link_valid_days": parameter(days(1), 30, "days"),
  // TODO(B-171): the spec of the Sales team shows the other brands too, below the active and target ones; whether they
  // stay is asked of Mattia. On until the answer, as the page has always been (ADR-312)
  /**
   * Whether the page of a customer's booking link offers the brands its contact has no interest for, besides the active
   * and target ones (ADR-312): off, only those, unless it has none in the campaign.
   */
  "booking.client_link_other_brands": parameter(z.boolean(), true),
  // TODO(T-63): TIL has not said whether it can send how many records HubSpot has of a kind; until then the share is
  // of the records Nexxt has (ADR-178 §4.4, ADR-191)
  /**
   * The most a complete full reading of HubSpot archives by itself, as a percentage of the records of its kind Nexxt
   * has: beyond it the reading waits for a person (ADR-191). 0 holds every reading that would archive anything.
   */
  "hubspot.full_reading_archive_max_percent": parameter(z.number().min(0).max(100), 5),
  /** Minutes before an appointment in the showroom at which its customer, and only it, is reminded of it (B-39). */
  "booking.reminder_minutes_before": parameter(minutes, 24 * 60, "minutes"),
  /** Minutes after which a row of the outbox still not delivered is a problem (FR-M16-04). */
  "integration.stalled_minutes": parameter(minutes, 15, "minutes"),
  /** Minutes TIL has to send the outcome of a message, where the message has no time of its own. */
  "integration.outcome_overdue_minutes": parameter(minutes, TIL_OUTCOME_MINUTES, "minutes"),
} as const;

export type ParameterKey = keyof typeof parameters;
export const parameterKeys = Object.keys(parameters) as [ParameterKey, ...ParameterKey[]];

export type ParameterValue<K extends ParameterKey = ParameterKey> = z.output<(typeof parameters)[K]["value"]>;

/**
 * The value of a parameter as a domain uses it, from what `platform.config_parameter` holds: one never set, or
 * holding a value the domain does not understand, is the default.
 */
export function parameterValue<K extends ParameterKey>(key: K, stored: unknown): ParameterValue<K> {
  const { value, default: fallback } = parameters[key];
  const parsed = value.safeParse(stored);
  return (parsed.success ? parsed.data : fallback) as ParameterValue<K>;
}

/** The parameters in minutes: how long something may wait before it is late. */
export type MinutesParameterKey = {
  [K in ParameterKey]: (typeof parameters)[K] extends { unit: "minutes" } ? K : never;
}[ParameterKey];

/**
 * The time a parameter in minutes cuts off before `now`, from its value as stored (its default when unset or odd):
 * what waits since before it is late.
 */
export const minutesBefore = (key: MinutesParameterKey, stored: unknown, now: Date): Date =>
  new Date(now.getTime() - Number(parameterValue(key, stored)) * 60_000);

/**
 * Key of the minutes after which the outcome of a message to TIL is overdue. The error queue of M16 and every domain
 * that lets a state be sent again go by the same, so an entity can be sent again exactly when its message shows
 * there as overdue.
 */
export const OUTCOME_OVERDUE_MINUTES = "integration.outcome_overdue_minutes" satisfies ParameterKey;

/** Key of the switch of the units on hold: sales and logistics subtract them, or neither does (ADR-145). */
export const SUBTRACT_UNITS_ON_HOLD = "stock.subtract_units_on_hold" satisfies ParameterKey;
/** ADR-234: whether I-06 raises and withdraws the flag "payment completed" of an order. */
export const PAYMENT_FLAG_FROM_BC = "sales_order.payment_flag_from_bc" satisfies ParameterKey;

/**
 * Whether the outcome of a message asked at `requestedAt` is overdue: past the time its type has in the contract,
 * otherwise past the parameter as stored. An outcome that never arrives does not hold an entity forever.
 */
export function outcomeOverdue(
  message: { outcomeMinutes?: number | undefined },
  asked: { requestedAt: string; overdueMinutes: unknown },
  now: Date,
): boolean {
  const minutes = message.outcomeMinutes ?? parameterValue(OUTCOME_OVERDUE_MINUTES, asked.overdueMinutes);
  return now.getTime() - Date.parse(asked.requestedAt) > minutes * 60_000;
}
